import { useQuery } from "@tanstack/react-query";
import { useProject } from "./useProject";
import { useQuestionMappings } from "./useQuestionMappings";

export interface FormRelationship {
  mainForm: {
    id: string;
    title: string | null;
    isReferral: boolean;
  } | null;
  referralForms: Array<{
    id: string;
    title: string | null;
    isReferral: boolean;
  }>;
}

export interface FormQuestionData {
  formId: string;
  formTitle: string | null;
  isReferral: boolean;
  questions: any[];
  groups: any;
  mapping: any;
}

/**
 * Hook que obtiene información de formularios principales y sus referidos
 */
export function useFormRelationships(jotformFormId?: string | null) {
  const { projectClient, currentProject } = useProject();

  // Obtener información sobre las relaciones entre formularios
  const { data: relationship, isLoading: isLoadingRelationship } = useQuery({
    queryKey: ['form-relationships', jotformFormId, currentProject],
    queryFn: async () => {
      if (!jotformFormId || !currentProject) return null;

      const projectId = currentProject === '3m' ? 1 : 2;

      // Obtener información del formulario actual
      const { data: currentForm, error: currentFormError } = await projectClient
        .from('jotform_form_mappings')
        .select(`
          id,
          jotform_form_id,
          form_title,
          is_referral_form,
          parent_form_id,
          parent:parent_form_id(
            id,
            jotform_form_id,
            form_title,
            is_referral_form
          )
        `)
        .eq('jotform_form_id', jotformFormId)
        .eq('project_id', projectId)
        .single();

      if (currentFormError) throw currentFormError;
      if (!currentForm) return null;

      let mainForm = null;
      let referralForms: any[] = [];

      if (currentForm.is_referral_form) {
        // Si el formulario actual es de referidos, obtener el principal
        if (currentForm.parent && Array.isArray(currentForm.parent) && currentForm.parent.length > 0) {
          const parentForm = currentForm.parent[0];
          mainForm = {
            id: parentForm.jotform_form_id,
            title: parentForm.form_title,
            isReferral: false
          };
        } else {
          // FALLBACK: Si es referido pero no tiene padre, tratarlo como principal
          console.warn('⚠️ Formulario marcado como referido pero sin padre. Tratándolo como principal.');
          mainForm = {
            id: currentForm.jotform_form_id,
            title: currentForm.form_title,
            isReferral: false
          };
        }
        
        // También incluir el formulario actual como referido
        referralForms.push({
          id: currentForm.jotform_form_id,
          title: currentForm.form_title,
          isReferral: true
        });

        // Obtener otros formularios referidos del mismo padre
        if (currentForm.parent_form_id) {
          const { data: siblingReferrals, error: siblingsError } = await projectClient
            .from('jotform_form_mappings')
            .select('jotform_form_id, form_title, is_referral_form')
            .eq('parent_form_id', currentForm.parent_form_id)
            .eq('project_id', projectId)
            .neq('jotform_form_id', jotformFormId); // Excluir el actual

          if (!siblingsError && siblingReferrals) {
            referralForms.push(...siblingReferrals.map(ref => ({
              id: ref.jotform_form_id,
              title: ref.form_title,
              isReferral: ref.is_referral_form
            })));
          }
        }
      } else {
        // Si el formulario actual es principal
        mainForm = {
          id: currentForm.jotform_form_id,
          title: currentForm.form_title,
          isReferral: false
        };

        // Obtener todos sus formularios referidos
        const { data: childReferrals, error: childrenError } = await projectClient
          .from('jotform_form_mappings')
          .select('jotform_form_id, form_title, is_referral_form')
          .eq('parent_form_id', currentForm.id)
          .eq('project_id', projectId);

        if (!childrenError && childReferrals) {
          referralForms = childReferrals.map(ref => ({
            id: ref.jotform_form_id,
            title: ref.form_title,
            isReferral: ref.is_referral_form
          }));
        }
      }

      return {
        mainForm,
        referralForms
      } as FormRelationship;
    },
    enabled: !!jotformFormId && !!currentProject,
  });

  return {
    relationship,
    isLoadingRelationship
  };
}

/**
 * Hook que obtiene las preguntas y grupos de ambos formularios (principal y referidos)
 */
export function useFormQuestionsData(jotformFormId?: string | null) {
  const { relationship, isLoadingRelationship } = useFormRelationships(jotformFormId);
  
  // Obtener preguntas del formulario principal
  const mainFormQuestions = useQuestionMappings(relationship?.mainForm?.id);
  
  // Para simplificar, obtenemos preguntas del primer formulario de referidos
  // En el futuro se podría extender para manejar múltiples formularios de referidos
  const firstReferralFormId = relationship?.referralForms?.[0]?.id;
  const referralFormQuestions = useQuestionMappings(firstReferralFormId);

  // Si el formulario pasado es de referidos, también obtener las preguntas del formulario original
  const originalFormQuestions = useQuestionMappings(jotformFormId);

  console.log('🔍 [useFormQuestionsData] Debug completo:', {
    jotformFormId,
    relationship: relationship?.mainForm,
    mainFormQuestions: {
      isLoading: mainFormQuestions.isLoading,
      hasMapping: !!mainFormQuestions.mapping,
      hasQuestions: !!mainFormQuestions.formQuestions?.length,
      questionsCount: mainFormQuestions.formQuestions?.length || 0
    },
    originalFormQuestions: {
      isLoading: originalFormQuestions.isLoading,
      hasMapping: !!originalFormQuestions.mapping,
      hasQuestions: !!originalFormQuestions.formQuestions?.length,
      questionsCount: originalFormQuestions.formQuestions?.length || 0
    },
    referralFormQuestions: {
      isLoading: referralFormQuestions.isLoading,
      hasMapping: !!referralFormQuestions.mapping,
      hasQuestions: !!referralFormQuestions.formQuestions?.length,
      questionsCount: referralFormQuestions.formQuestions?.length || 0
    }
  });

  const isLoading = isLoadingRelationship || mainFormQuestions.isLoading || referralFormQuestions.isLoading;

  const formsData: FormQuestionData[] = [];

  // Agregar datos del formulario principal PRIMERO
  if (relationship?.mainForm) {
    // Usar las preguntas del formulario principal si están disponibles, sino usar las del formulario original
    const mainFormData = mainFormQuestions.mapping ? mainFormQuestions : originalFormQuestions;
    
    console.log('🔍 [useFormQuestionsData] Datos del formulario principal:', {
      mainFormQuestions: mainFormQuestions.mapping ? 'disponible' : 'no disponible',
      originalFormQuestions: originalFormQuestions.mapping ? 'disponible' : 'no disponible',
      usingData: mainFormQuestions.mapping ? 'mainFormQuestions' : 'originalFormQuestions'
    });
    
    if (mainFormData.mapping) {
      formsData.push({
        formId: relationship.mainForm.id,
        formTitle: relationship.mainForm.title,
        isReferral: false,
        questions: mainFormData.formQuestions || [],
        groups: mainFormData.mapping?.mapping_config?.groups || {},
        mapping: mainFormData.mapping
      });
      console.log('✅ [useFormQuestionsData] Formulario principal agregado:', relationship.mainForm.id);
    } else if (mainFormQuestions.formQuestions && mainFormQuestions.formQuestions.length > 0) {
      // Si no hay mapeo pero sí hay preguntas, crear un mapeo temporal
      console.log('🔧 [useFormQuestionsData] Creando mapeo temporal para formulario principal...');
      
      // Crear grupos básicos basados en las preguntas
      const basicGroups = {
        "General": {
          questions: mainFormQuestions.formQuestions.map(q => q.name),
          subgroups: {}
        }
      };
      
      formsData.push({
        formId: relationship.mainForm.id,
        formTitle: relationship.mainForm.title,
        isReferral: false,
        questions: mainFormQuestions.formQuestions || [],
        groups: basicGroups,
        mapping: {
          id: 0, // Temporal
          project_id: 1,
          jotform_form_id: relationship.mainForm.id,
          form_mapping_id: null,
          mapping_config: { groups: basicGroups },
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        }
      });
      console.log('✅ [useFormQuestionsData] Formulario principal agregado con mapeo temporal');
    } else {
      console.log('❌ [useFormQuestionsData] No se pudieron obtener datos del formulario principal');
    }
  }

  // Agregar datos del formulario de referidos SEGUNDO
  if (relationship?.referralForms?.[0] && referralFormQuestions.mapping) {
    formsData.push({
      formId: relationship.referralForms[0].id,
      formTitle: relationship.referralForms[0].title,
      isReferral: true,
      questions: referralFormQuestions.formQuestions || [],
      groups: referralFormQuestions.mapping?.mapping_config?.groups || {},
      mapping: referralFormQuestions.mapping
    });
  }

  return {
    relationship,
    formsData,
    isLoading,
    mainFormQuestions: mainFormQuestions.formQuestions || [],
    referralFormQuestions: referralFormQuestions.formQuestions || [],
    mainFormGroups: mainFormQuestions.mapping?.mapping_config?.groups || {},
    referralFormGroups: referralFormQuestions.mapping?.mapping_config?.groups || {},
  };
}